import { addDays, compareIso, isMonthEnd } from "./dates";
import type { ProjectionRow, Transaction } from "./types";

export function simulateBwz(
  transactions: Transaction[],
  startDate: string,
  endDate: string,
  opts: {
    bausparsumme: number;
    annual_interest_rate: number;
    zinsfaktor: number;
    divisor_per_bausparsumme: number;
    min_savings_ratio: number;
    target_bwz: number;
  },
): ProjectionRow[] {
  const {
    bausparsumme,
    annual_interest_rate,
    zinsfaktor,
    divisor_per_bausparsumme,
    min_savings_ratio,
    target_bwz,
  } = opts;

  const txByDay = new Map<string, Transaction[]>();
  for (const tx of transactions) {
    if (
      compareIso(startDate, tx.value_date) <= 0 &&
      compareIso(tx.value_date, endDate) <= 0
    ) {
      const list = txByDay.get(tx.value_date) ?? [];
      list.push(tx);
      txByDay.set(tx.value_date, list);
    }
  }

  let balance = 0;
  let cumulativeInterest = 0;
  const rows: ProjectionRow[] = [];
  let d = startDate;

  while (compareIso(d, endDate) <= 0) {
    const todaysTxs = txByDay.get(d) ?? [];
    const txSum = todaysTxs.reduce((s, t) => s + t.amount, 0);
    if (todaysTxs.length > 0) {
      balance += txSum;
    }

    const dailyInterest = (balance * annual_interest_rate) / 365;
    cumulativeInterest += dailyInterest;

    const isTxDay = todaysTxs.length > 0;
    const isEnd = d === endDate;
    if (isMonthEnd(d) || isTxDay || isEnd) {
      const bwz =
        (balance + zinsfaktor * cumulativeInterest) /
        (divisor_per_bausparsumme * bausparsumme);
      rows.push({
        date: d,
        transaction_sum: round2(txSum),
        transaction_texts: todaysTxs.map((t) => t.text).join("; "),
        balance: round2(balance),
        cumulative_interest_for_bwz: round6(cumulativeInterest),
        bwz: round4(bwz),
        min_savings_required: round2(min_savings_ratio * bausparsumme),
        min_savings_met: balance >= min_savings_ratio * bausparsumme,
        target_bwz,
        target_bwz_met: bwz >= target_bwz,
        zuteilung_estimate_met:
          balance >= min_savings_ratio * bausparsumme && bwz >= target_bwz,
      });
    }

    d = addDays(d, 1);
  }

  return rows;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function round6(n: number): number {
  return Math.round(n * 1_000_000) / 1_000_000;
}
